import { legacyHookAttrs } from '@/msgdeskRuntime/legacyBridge/SelectorContracts'
import { dispatchEditorAction } from '@/msgdeskRuntime/editorActions'
import { selectAccessState, selectMainState, selectSubRenderState, type SubRenderState } from '@/msgdeskRuntime/state'
import { useSubsStore } from '@/state/MsgTreeProvider'
import { showQuickReplyModal } from '@/deskUiSkeletons/Shared/QuickReplyModal'
import { LockedScreen } from '@/deskUiSkeletons/Blocking/LockedScreen'
import { EmptyContent } from '@/deskUiSkeletons/Page/MainContent'
import type { MailboxPreviewBody } from './mailboxPreviewLoader'
import { MailboxIcon } from './MailboxIcons'
import { pushMailboxEditorUrl } from '../mailboxRoutes'
import { ContentView, resolveContentDocument } from '@/app/richEdit/content'
import { EmailMessageView, type EmailMessageViewModel } from '../../components/email/EmailMessageView'

function clean(value: unknown) {
  return typeof value === 'string' || typeof value === 'number' ? String(value).trim() : ''
}

function record(value: unknown): Record<string, any> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, any> : {}
}

function firstClean(...values: unknown[]) {
  for (const value of values) {
    const text = clean(value)
    if (text) return text
  }
  return ''
}

function initial(value: string) {
  return value.trim().slice(0, 1).toUpperCase() || 'M'
}

function openMailboxEdit() {
  dispatchEditorAction({ action: 'open-edit-message' })
  pushMailboxEditorUrl('update_msg')
}

function openMailboxReply() {
  dispatchEditorAction({ action: 'open-reply' })
}

function selectedSubPreview(sub: SubRenderState | null) {
  if (!sub) return null
  const legacy = record(sub.legacy.data)
  const facade = record(legacy.facade)
  const content = record(facade.content)
  const identity = record(facade.identity)
  const email = record(facade.email)
  const time = record(facade.time)
  const avatar = record(facade.avatar)

  return {
    author: firstClean(sub.author, identity.display_name, identity.name_key, legacy.name_key, legacy.code_key, sub.codeKey) || 'Unknown sender',
    address: firstClean(identity.address, email.from_display, email.from, legacy.email_from, legacy.email_to, sub.codeKey ? `${sub.codeKey}@msgdesk.com` : ''),
    subject: firstClean(sub.title, content.subject, legacy.subject, legacy.title, legacy.namekey_subject, legacy.name_key) || '(No subject)',
    datetime: firstClean(sub.datetime, time.formatted, time.create_time, legacy.datetime, legacy.create_time, legacy.created_time, legacy.timestart_str, legacy.timestart),
    bodyHtml: firstClean(sub.html, content.html, legacy.html, legacy.body_html, legacy.txt_html, legacy.msg_inputs?.body_textarea?.[1]?.msg_text),
    body: firstClean(sub.text, content.text, legacy.text, legacy.body, legacy.msg_text, legacy.txt),
    avatarPath: firstClean(sub.avatarPath, avatar.path, legacy.avatar_path),
  }
}

export function MailboxMainMessage({
  selectedSubId,
  previewBody,
  isPreviewLoading = false,
  previewError,
  isPreviewCollapsed = false,
  onTogglePreview,
}: {
  selectedSubId?: number | null
  previewBody?: MailboxPreviewBody | null
  isPreviewLoading?: boolean
  previewError?: string | null
  isPreviewCollapsed?: boolean
  onTogglePreview?: () => void
}) {
  const { state } = useSubsStore()
  const main = selectMainState(state)
  const selectedSub = selectedSubId ? selectSubRenderState(state, selectedSubId) : null
  const subPreview = selectedSubPreview(selectedSub)
  const access = selectAccessState(state)
  const mailboxPreview = selectedSubId ? previewBody : null
  const author = mailboxPreview?.author || subPreview?.author || clean(main.author || main.nameKey || main.codeKey) || 'Unknown sender'
  const address = mailboxPreview?.address || subPreview?.address || clean(main.fields?.emailAddress) || (main.codeKey ? `${main.codeKey}@msgdesk.com` : '')
  const subject = mailboxPreview?.subject || subPreview?.subject || clean(main.subject || main.title) || '(No subject)'
  const datetime = mailboxPreview?.datetime || subPreview?.datetime || clean(main.datetime)
  const bodyHtml = mailboxPreview?.bodyHtml || subPreview?.bodyHtml || clean(main.bodyHtml)
  const body = mailboxPreview?.body || subPreview?.body || clean(main.body)
  const avatarPath = mailboxPreview?.avatarPath || subPreview?.avatarPath || clean(main.avatarPath)
  // Source selection (the preview chains above) stays mailbox-owned; format
  // selection and safe rendering are the content package's job.
  const contentDoc = resolveContentDocument({ bodyHtml, body })

  if (isPreviewCollapsed) {
    return (
      <aside className="md-mailbox-preview-collapsed flex min-h-0 flex-col items-center border-l border-[#cbd5df] bg-[#f8fafb] shadow-[-1px_0_0_rgba(23,33,43,0.04)]">
        <button
          type="button"
          className="mt-3 flex h-8 w-8 items-center justify-center rounded-md border border-[#c8d2dc] bg-white text-[#314253] shadow-sm hover:bg-[#edf2f6]"
          onClick={onTogglePreview}
          title="Show preview"
        >
          <MailboxIcon name="chevronLeft" className="h-4 w-4" />
        </button>
      </aside>
    )
  }

  const isEmailPreview = Boolean(
    selectedSubId
    && mailboxPreview
    && (mailboxPreview.msgType === 'email' || mailboxPreview.emailMessage || mailboxPreview.from.length || mailboxPreview.to.length),
  )
  if (isEmailPreview && mailboxPreview) {
    const emailModel: EmailMessageViewModel = {
      subject: mailboxPreview.emailMessage?.subject || mailboxPreview.subject,
      body: mailboxPreview.body,
      bodyHtml: mailboxPreview.bodyHtml,
      datetime: mailboxPreview.datetime,
      from: mailboxPreview.from,
      to: mailboxPreview.to,
      cc: mailboxPreview.emailMessage?.cc ?? [],
      bcc: mailboxPreview.emailMessage?.bcc ?? [],
      replyTo: mailboxPreview.emailMessage?.replyTo ?? [],
      metadata: mailboxPreview.emailMessage,
      attachments: mailboxPreview.attachments,
      showUnlocked: access.showUnlocked,
    }
    return (
      <article {...legacyHookAttrs('main.root', 'md-mailbox-message flex min-h-0 min-w-[360px] flex-1 flex-col border-l border-[#cbd5df] bg-white shadow-[-1px_0_0_rgba(23,33,43,0.04)] max-[980px]:min-w-0')}>
        <div className="flex h-11 flex-[0_0_44px] items-center justify-between border-b border-[#d9e1e8] bg-[#f8fafb] px-4">
          <div className="min-w-0 truncate text-[12px] font-semibold uppercase tracking-[0.02em] text-[#687684]">Email preview · read only</div>
          <button
            type="button"
            className="flex h-8 w-8 items-center justify-center rounded-md border border-[#c8d2dc] bg-white text-[#314253] shadow-sm hover:bg-[#edf2f6]"
            onClick={onTogglePreview}
            title="Hide preview"
          >
            <MailboxIcon name="chevronRight" className="h-4 w-4" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-auto">
          <EmailMessageView model={emailModel} embedded />
        </div>
      </article>
    )
  }

  return (
    <article {...legacyHookAttrs('main.root', 'md-mailbox-message flex min-h-0 min-w-[360px] flex-1 flex-col border-l border-[#cbd5df] bg-white shadow-[-1px_0_0_rgba(23,33,43,0.04)] max-[980px]:min-w-0')}>
      <div className="flex h-11 flex-[0_0_44px] items-center justify-between border-b border-[#d9e1e8] bg-[#f8fafb] px-4">
        <div className="min-w-0 truncate text-[12px] font-semibold uppercase tracking-[0.02em] text-[#687684]">{selectedSubId ? 'Sub preview' : 'Page preview'}</div>
        <button
          type="button"
          className="flex h-8 w-8 items-center justify-center rounded-md border border-[#c8d2dc] bg-white text-[#314253] shadow-sm hover:bg-[#edf2f6]"
          onClick={onTogglePreview}
          title="Hide preview"
        >
          <MailboxIcon name="chevronRight" className="h-4 w-4" />
        </button>
      </div>
      <header {...legacyHookAttrs('main.header', '!block border-b border-[#d9e1e8] bg-white px-6 py-5 max-[760px]:px-4')}>
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" className="flex h-8 items-center gap-2 rounded-md bg-[#2563eb] px-3 text-[12px] font-semibold text-white shadow-sm hover:bg-[#1d4ed8]" onClick={openMailboxReply}>
              <MailboxIcon name="reply" className="h-4 w-4" />
              Reply
            </button>
            <button type="button" className="flex h-8 items-center gap-2 rounded-md border border-[#c8d2dc] bg-white px-3 text-[12px] font-medium text-[#314253] shadow-sm hover:bg-[#f2f5f7]" onClick={() => showQuickReplyModal(selectedSubId || 0, Boolean(selectedSubId))}>
              <MailboxIcon name="message" className="h-4 w-4" />
              Quick reply
            </button>
            {access.editUnlocked && !selectedSubId ? (
              <button type="button" className="flex h-8 items-center gap-2 rounded-md border border-[#c8d2dc] bg-white px-3 text-[12px] font-medium text-[#314253] shadow-sm hover:bg-[#f2f5f7]" onClick={openMailboxEdit}>
                <MailboxIcon name="edit" className="h-4 w-4" />
                Edit
              </button>
            ) : null}
          </div>
        </div>
        <h1 {...legacyHookAttrs('main.subjectText', 'break-words text-[22px] font-semibold leading-7 text-[#17212b]')}>{subject}</h1>
        <div className="mt-5 flex items-start gap-3 rounded-md border border-[#e4eaf0] bg-[#fafcff] p-3">
          <div {...legacyHookAttrs('main.avatarFrame', 'relative flex h-11 w-11 flex-none items-center justify-center overflow-hidden rounded-full bg-[#2f6f62] font-semibold text-white shadow-sm')}>
            <span>{initial(author)}</span>
            {avatarPath ? <img className="absolute inset-0 h-full w-full object-cover" src={avatarPath} alt="" onError={(event) => { event.currentTarget.style.display = 'none' }} /> : null}
          </div>
          <div className="min-w-0 flex-1">
            <div {...legacyHookAttrs('main.authorFrame', 'break-words text-[14px] font-semibold text-[#17212b]')}>{author}</div>
            {address ? <div {...legacyHookAttrs('main.emailText', 'break-all text-[12px] text-[#687684]')}>{address}</div> : null}
          </div>
          <time {...legacyHookAttrs('main.datetime', 'flex-none text-[12px] text-[#687684]')}>{datetime}</time>
        </div>
      </header>

      <div {...legacyHookAttrs('main.body', '!block min-h-0 flex-1 overflow-auto')}>
        <div {...legacyHookAttrs('main.iframe', 'md-mailbox-message-body mx-auto max-w-[880px] px-8 py-7 text-[14px] leading-6 text-[#26323d] max-[760px]:px-4 [&_a]:text-[#1f62a6] [&_img]:max-w-full')}>
          {previewError ? (
            <div className="rounded border border-[#f1c6c6] bg-[#fff7f7] p-4 text-[#8a3333]">{previewError}</div>
          ) : isPreviewLoading ? (
            <div className="rounded-md border border-[#d8dee4] bg-[#f8fafc] p-4">
              <div className="mb-3 h-3 w-2/3 rounded bg-[#dbe3ec]" />
              <div className="mb-2 h-3 w-full rounded bg-[#e4ebf2]" />
              <div className="mb-2 h-3 w-5/6 rounded bg-[#e4ebf2]" />
              <div className="h-3 w-3/5 rounded bg-[#e4ebf2]" />
            </div>
          ) : access.showUnlocked ? (
            contentDoc ? <ContentView doc={contentDoc} /> : <EmptyContent label={selectedSubId ? 'No preview content for this sub yet.' : 'No preview content for this page yet.'} />
          ) : (
            <LockedScreen />
          )}
        </div>
        <div {...legacyHookAttrs('main.editMount', 'hidden')} />
      </div>
    </article>
  )
}
